import { useCallback, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/icons';
import { AsyncPanel } from '@/components/platform/AsyncPanel';
import { StateBadge } from '@/components/platform/StateBadge';
import { InstitutionPicker } from '@/components/platform/InstitutionPicker';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import { useToast } from '@/components/ui/Toast';
import { useAsyncData } from '@/hooks';
import { academicPoliciesApi } from '@/api/config-governance';
import {
  LIFECYCLE_STATUSES,
  POLICY_DOMAINS,
  type AcademicPolicy,
  type PolicyDomain,
} from '@/types/config-governance';
import { formatDate } from '@/utils/format';
import styles from './platform-pages.module.css';
import gov from './governance.module.css';

/**
 * CFG-12 — the academic policy register, grouped by domain.
 *
 * Grouped rather than flat because the operator's question always arrives
 * domain-first — "what is the repetition policy?" — and fourteen domains of
 * mixed versions in one list would bury the answer. Within a domain, versions
 * sort newest first (the server's order), so the top row of each group is the
 * one that matters now.
 */

type ScopeChoice = 'all' | 'platform' | 'institution';

export function AcademicPoliciesPage() {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const domain = params.get('domain') ?? '';
  const status = params.get('status') ?? '';
  const scopeParam = params.get('scope') ?? '';
  const scope: ScopeChoice =
    scopeParam === 'platform' ? 'platform' : scopeParam ? 'institution' : 'all';
  const scopeInstitution = scope === 'institution' ? scopeParam.trim() : '';
  const [creating, setCreating] = useState(false);

  const fetcher = useCallback(() => {
    return academicPoliciesApi.list({
      ...(domain ? { policyDomain: domain as PolicyDomain } : {}),
      ...(status ? { status } : {}),
      ...(scope === 'platform'
        ? { institutionId: 'platform' }
        : scope === 'institution' && scopeInstitution
          ? { institutionId: scopeInstitution }
          : {}),
    });
  }, [domain, status, scope, scopeInstitution]);

  const { state, reload } = useAsyncData<AcademicPolicy[]>(
    `academic-policies:${domain}:${status}:${scopeParam}`,
    fetcher,
  );

  const setFilters = (nextDomain: string, nextStatus: string, nextScope: string) => {
    const next = new URLSearchParams();
    if (nextDomain) next.set('domain', nextDomain);
    if (nextStatus) next.set('status', nextStatus);
    if (nextScope) next.set('scope', nextScope);
    setParams(next);
  };

  const groups = useMemo(() => {
    if (state.status !== 'ready') return [];
    const byDomain = new Map<string, AcademicPolicy[]>();
    for (const policy of state.data) {
      const list = byDomain.get(policy.policyDomain) ?? [];
      list.push(policy);
      byDomain.set(policy.policyDomain, list);
    }
    // Enum order, not alphabetical: the fourteen domains follow the student
    // journey (admission → enrolment → … → certification) and the register
    // should read the same way.
    return POLICY_DOMAINS.filter((d) => byDomain.has(d)).map(
      (d) => [d, byDomain.get(d) as AcademicPolicy[]] as const,
    );
  }, [state]);

  return (
    <div className={styles.page}>
      <header className={styles.pageHeader}>
        <div>
          <h1 className={styles.pageTitle}>{t('acadPolicies.title')}</h1>
          <p className={styles.pageLead}>{t('acadPolicies.lead')}</p>
        </div>
        <button type="button" className={styles.primaryBtn} onClick={() => setCreating(true)}>
          <Icon name="plus" size={16} />
          {t('acadPolicies.newPolicy')}
        </button>
      </header>

      <div className={styles.filterBar} role="search">
        <div className={styles.filterField}>
          <label className={styles.filterLabel} htmlFor="policy-domain">
            {t('acadPolicies.filterDomain')}
          </label>
          <Select
            id="policy-domain"
            value={domain}
            onChange={(next) => setFilters(next, status, scopeParam)}
            searchable
            options={[
              { value: '', label: t('acadPolicies.filterAllDomains') },
              ...POLICY_DOMAINS.map((value) => ({
                value,
                label: t(`acadPolicies.domains.${value}`, { defaultValue: value }),
              })),
            ]}
          />
        </div>
        <div className={styles.filterField}>
          <label className={styles.filterLabel} htmlFor="policy-status">
            {t('acadPolicies.filterStatus')}
          </label>
          <Select
            id="policy-status"
            value={status}
            onChange={(next) => setFilters(domain, next, scopeParam)}
            options={[
              { value: '', label: t('grading.filterAllStatuses') },
              ...LIFECYCLE_STATUSES.map((value) => ({
                value,
                label: t(`platform.state.lifecycle.${value}`),
              })),
            ]}
          />
        </div>
        <div className={styles.filterField}>
          <label className={styles.filterLabel} htmlFor="policy-scope">
            {t('grading.filterScope')}
          </label>
          <Select
            id="policy-scope"
            value={scope}
            onChange={(next) =>
              setFilters(domain, status, next === 'all' ? '' : next === 'platform' ? 'platform' : ' ')
            }
            options={[
              { value: 'all', label: t('grading.scopeAll') },
              { value: 'platform', label: t('grading.scopePlatform') },
              { value: 'institution', label: t('grading.scopeInstitution') },
            ]}
          />
        </div>
        {scope === 'institution' && (
          <div className={styles.filterField}>
            <label className={styles.filterLabel} htmlFor="policy-institution">
              {t('grading.institutionLabel')}
            </label>
            <InstitutionPicker
              id="policy-institution"
              value={scopeInstitution}
              onChange={(next) => setFilters(domain, status, next || ' ')}
            />
          </div>
        )}
      </div>

      <AsyncPanel
        state={state}
        onRetry={reload}
        isEmpty={(data) => data.length === 0}
        emptyTitle={t('acadPolicies.emptyTitle')}
        emptyBody={t('acadPolicies.emptyBody')}
      >
        {() => (
          <>
            {groups.map(([groupDomain, policies]) => (
              <section key={groupDomain}>
                <h2 className={styles.subheading}>
                  {t(`acadPolicies.domains.${groupDomain}`, { defaultValue: groupDomain })}
                </h2>
                <ul className={styles.rowList}>
                  {policies.map((policy) => (
                    <li key={policy.id} className={styles.row}>
                      <div className={styles.rowMain}>
                        <Link
                          className={styles.rowTitleLink}
                          to={`/admin/academic-policies/${encodeURIComponent(policy.id)}`}
                        >
                          {policy.title}
                        </Link>
                        <div className={styles.rowMetaLine}>
                          <span className={styles.mutedTag}>
                            {t('grading.versionShort', { n: policy.version })}
                          </span>
                          <StateBadge kind="lifecycle" value={policy.status} size="sm" />
                          <span className={gov.scopeTag}>
                            {policy.institutionId === null
                              ? t('grading.scopePlatform')
                              : t('grading.scopeInstitution')}
                          </span>
                        </div>
                        {policy.changeSummary && (
                          <p className={styles.rowDetail}>{policy.changeSummary}</p>
                        )}
                      </div>
                      <div className={styles.rowAside}>
                        <span className={styles.mutedCell}>
                          {t('acadPolicies.effectiveFrom', {
                            when: formatDate(policy.effectiveFrom, i18n.language),
                          })}
                        </span>
                      </div>
                    </li>
                  ))}
                </ul>
              </section>
            ))}
          </>
        )}
      </AsyncPanel>

      <CreatePolicyDialog
        open={creating}
        onClose={() => setCreating(false)}
        onCreated={(created) => {
          setCreating(false);
          toast.success(
            t('acadPolicies.createdToast', {
              domain: t(`acadPolicies.domains.${created.policyDomain}`, {
                defaultValue: created.policyDomain,
              }),
              n: created.version,
            }),
          );
          navigate(`/admin/academic-policies/${encodeURIComponent(created.id)}`);
        }}
      />
    </div>
  );
}

function CreatePolicyDialog({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (created: AcademicPolicy) => void;
}) {
  const { t } = useTranslation();
  const [policyDomain, setPolicyDomain] = useState<PolicyDomain>('ATTENDANCE');
  const [title, setTitle] = useState('');
  const [effectiveFrom, setEffectiveFrom] = useState('');
  const [changeSummary, setChangeSummary] = useState('');
  const [institutionId, setInstitutionId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canSubmit =
    title.trim().length > 0 && /^\d{4}-\d{2}-\d{2}$/.test(effectiveFrom) && !submitting;

  const submit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      // rules starts as {} on purpose: the fourteen domains do not share a
      // shape, and the structured JSON editor on the detail page is where the
      // domain document gets written. An empty object is a valid draft.
      const created = await academicPoliciesApi.create({
        policyDomain,
        title: title.trim(),
        effectiveFrom,
        rules: {},
        ...(changeSummary.trim() ? { changeSummary: changeSummary.trim() } : {}),
        ...(institutionId ? { institutionId } : {}),
      });
      onCreated(created);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('acadPolicies.createTitle')}
      description={t('acadPolicies.createIntro')}
      closeOnBackdropClick={false}
    >
      <div className={styles.dialogForm}>
        <div className={styles.field}>
          <label className={styles.fieldLabel} htmlFor="new-policy-domain">
            {t('acadPolicies.fieldDomain')}
          </label>
          <Select
            id="new-policy-domain"
            value={policyDomain}
            onChange={(next) => setPolicyDomain(next as PolicyDomain)}
            searchable
            options={POLICY_DOMAINS.map((value) => ({
              value,
              label: t(`acadPolicies.domains.${value}`, { defaultValue: value }),
            }))}
          />
        </div>
        <div className={styles.field}>
          <label className={styles.fieldLabel} htmlFor="new-policy-title">
            {t('acadPolicies.fieldTitle')}
          </label>
          <input
            id="new-policy-title"
            className={gov.editorInput}
            value={title}
            onChange={(event) => setTitle(event.target.value)}
          />
        </div>
        <div className={styles.field}>
          <label className={styles.fieldLabel} htmlFor="new-policy-effective">
            {t('acadPolicies.fieldEffective')}
          </label>
          <p className={styles.fieldHint}>{t('acadPolicies.fieldEffectiveHint')}</p>
          <input
            id="new-policy-effective"
            className={gov.editorInputNarrow}
            type="date"
            value={effectiveFrom}
            onChange={(event) => setEffectiveFrom(event.target.value)}
          />
        </div>
        <div className={styles.field}>
          <label className={styles.fieldLabel} htmlFor="new-policy-summary">
            {t('acadPolicies.fieldSummary')}
          </label>
          <p className={styles.fieldHint}>{t('acadPolicies.fieldSummaryHint')}</p>
          <textarea
            id="new-policy-summary"
            className={styles.textarea}
            rows={2}
            value={changeSummary}
            onChange={(event) => setChangeSummary(event.target.value)}
          />
        </div>
        <div className={styles.field}>
          <label className={styles.fieldLabel} htmlFor="new-policy-institution">
            {t('grading.fieldScope')}
          </label>
          <p className={styles.fieldHint}>{t('grading.fieldScopeHint')}</p>
          <InstitutionPicker
            id="new-policy-institution"
            value={institutionId}
            clearable
            onChange={setInstitutionId}
            placeholder={t('grading.scopePlatform')}
          />
        </div>

        {error && (
          <p className={styles.formError} role="alert">
            {error}
          </p>
        )}

        <div className="modal-actions">
          <button type="button" className="modal-button modal-button--secondary" onClick={onClose} disabled={submitting}>
            {t('common.cancel')}
          </button>
          <button type="button" className="modal-button" disabled={!canSubmit} onClick={submit}>
            {submitting ? t('governance.transition.working') : t('acadPolicies.createConfirm')}
          </button>
        </div>
      </div>
    </Modal>
  );
}
